import { Box, Stack, Text } from '@atlaskit/primitives';
import { getDatabaseContainerType, type AnalyticsClient } from '@databases/analytics';
import { type MessageFormatter } from '@databases/intl/intl-types';
import { messages } from '@databases/intl/messages';
import {
  selectSelectedEntireFieldIds,
  selectSelectedFieldIds,
} from '@databases/redux/cellSelections/selectors';
import { doDeleteFields } from '@databases/redux/contextual-menu/actions';
import { appStore } from '@databases/redux/store';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React from 'react';
import { FormattedMessage } from 'react-intl-next';

import { type CellContextMenuContext } from '../types';

import { onMouseLeaveDeleteButton } from './on-mouse-leave-delete-button';

/**
 * Handles the deletion of one or more fields from a database, showing a confirmation dialog to the user.
 * If the user confirms, dispatches the action to delete the selected fields, hides the panel if necessary,
 * and sends analytics events for field deletion and calculation removal.
 */
export function deleteFields({
  contentId,
  formatMessage,
  client,
  showDialog,
  hidePanel,
  fieldId,
  panelFieldId,
}: {
  contentId: string;
  fieldId: string;
  panelFieldId: string;
  formatMessage: MessageFormatter;
  client: AnalyticsClient;
  showDialog: CellContextMenuContext['dialogs']['showDialog'];
  hidePanel: CellContextMenuContext['panelManager']['hidePanel'];
}) {
  const fieldNames: string[] = [];
  const database = selectDatabaseByContentIdRequired(appStore.getState(), {
    contentId,
  });
  const selectedEntireFieldIds = selectSelectedEntireFieldIds(appStore.getState(), {
    contentId,
  });

  selectedEntireFieldIds.forEach((fieldId) => {
    if (database.fieldsById[fieldId]) {
      fieldNames.push(database.fieldsById[fieldId].name);
    }
  });

  showDialog('ConfirmDialog', {
    heading: formatMessage(messages['field-settings.delete-field']),
    body: (
      <Stack space="space.150">
        <Box>
          <FormattedMessage
            {...messages[
              'field-settings.delete-multiple-fields-confirmation-message.fields'
            ]}
            values={{
              fieldsNumber: fieldNames.length,
              fields: <Text weight="bold">{fieldNames.join(', ')}</Text>,
            }}
          />
        </Box>
        <Box>
          {formatMessage(
            messages[
              'field-settings.delete-multiple-fields-confirmation-message.additional'
            ]
          )}
        </Box>
      </Stack>
    ),
    yesLabel: formatMessage(messages['delete-buttons.delete-field-button']),
    noLabel: formatMessage(messages['delete-buttons.delete-field-cancel-button']),
    appearance: 'danger',
    onClose: (result: boolean) => {
      onMouseLeaveDeleteButton();

      if (result) {
        const selectedFieldIds = selectSelectedFieldIds(appStore.getState(), {
          contentId,
        });

        if (selectedFieldIds.includes(panelFieldId)) {
          hidePanel();
        }

        appStore.dispatch(doDeleteFields(contentId));
      }
    },
  });

  const databaseContainer = getDatabaseContainerType();

  void client.sendDeleteFieldEvent({
    contentId,
    databaseContainer,
  });

  client.sendRemoveCalculationEvent({
    databaseContainer,
    fieldId,
    source: 'selectValueMenu',
    contentId: database.contentId,
  });
}
